/**
 * ==========================================================================
 *  Liane_Box.jsx — Liane-Pflegebox-Bereich (Phase 4, ab 2026-04-27)
 * ==========================================================================
 *  Dachseite fuer den Liane-Pflegebox-Geschaeftsbereich (Submarke der
 *  Toys UG x Pflege-Dschungel.de, Website liane-pflegebox.de, ~700 Kunden).
 *  Vier Sub-Tabs, alle noch Platzhalter — werden in spaeteren Schritten
 *  ausgebaut.
 *
 *  Sub-Tabs:
 *    - Kunden          ~700 Box-Kunden, spaeter Verknuepfung zu kunden
 *                      via versichertennummer + E-Mail
 *    - Produkte        Stammdaten der bestellbaren Hilfsmittel
 *    - FCA             Finance Controlling and Administration
 *                      (Bestellungen, Auftraege, Abrechnungen)
 *    - Bestell-Widget  Cockpit-Variante des bestehenden Bestelltools
 *                      (42-EUR-Budget + Antrag an die Krankenkasse)
 * ==========================================================================
 */

import React, { useEffect, useState } from 'react';
import { Package, ShoppingCart, Users, Wallet, Inbox, Loader2, FileText, Trash2, Plus, X, Search, Pencil, MapPin, Activity } from 'lucide-react';
import LianeProdukteVerwaltung from './Liane_Produkte';
import { api } from '../api';

const CI = { brombeer: '#85366C' };

const SUB_TABS = [
  { id: 'kunden',         label: 'Kunden',         icon: Users },
  { id: 'produkte',       label: 'Produkte',       icon: Package },
  { id: 'bestellungen',   label: 'Bestellungen',   icon: Inbox },
  { id: 'fca',            label: 'FCA',            icon: Wallet },
  { id: 'bestell-widget', label: 'Bestell-Widget', icon: ShoppingCart },
];

const PLATZHALTER_TEXTE = {
  'kunden':
    'Übersicht der ~700 aktiven Liane-Pflegebox-Kunden. Spätere ' +
    'Verknüpfung zu deinen Beratungskunden via Versicherungs-ID und ' +
    'E-Mail-Adresse — damit du im Beratungsgespräch sofort siehst, ' +
    'wer welche Box bezieht und welche Produkte er gerade verwendet.',
  'produkte':
    'Stammdaten der bestellbaren Pflegehilfsmittel: Bilder, Mengen ' +
    'pro Einheit, Konfigurations-Optionen (z.B. Material Nitril/Vinyl/' +
    'Latex, Größe S–XL bei Einmalhandschuhen).',
  'fca':
    'Finance Controlling and Administration: alle Bestellungen, ' +
    'Aufträge, Abrechnungen mit den Krankenkassen und Überweisungen ' +
    'rund um die Pflegebox.',
  'bestell-widget':
    'Cockpit-Variante des Liane-Pflegebox-Bestelltools. Schritt 1: ' +
    'Produkte zusammenstellen bis zum kostenfreien 42-€-Höchstbetrag ' +
    '(plus optional die wiederverwendbare Bettschutzeinlage, max. 4 ' +
    'pro Jahr). Schritt 2: Antrag mit Pflegegrad, Versichertennummer ' +
    'und Unterschrift erzeugen, der zur Krankenkasse geht.',
};

export default function LianeBox() {
  const [subTab, setSubTab] = useState('kunden');
  const aktiverText = PLATZHALTER_TEXTE[subTab] || '';
  const aktiverTitel = SUB_TABS.find(t => t.id === subTab)?.label || '';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b border-gray-200 px-6 py-2 flex gap-1 overflow-x-auto">
        {SUB_TABS.map(t => {
          const Icon = t.icon;
          const active = subTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors flex items-center gap-2 whitespace-nowrap ${
                active ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
              }`}
              style={active ? { backgroundColor: CI.brombeer } : {}}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {subTab === 'kunden' ? (
        <div className="flex-1 overflow-y-auto">
          <LianeKundenAnsicht />
        </div>
      ) : subTab === 'produkte' ? (
        <div className="flex-1 overflow-y-auto">
          <LianeProdukteVerwaltung />
        </div>
      ) : subTab === 'bestellungen' ? (
        <div className="flex-1 overflow-y-auto">
          <LianeBestellungenAnsicht />
        </div>
      ) : subTab === 'bestell-widget' ? (
        <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
          <div className="max-w-7xl mx-auto bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
            <div className="px-4 py-2 border-b border-gray-200 bg-amber-50 text-xs font-bold text-amber-900">
              Live-Vorschau · Produktauswahl kommt aus dem Cockpit-Katalog · oeffentliche URL: <a href="/liane-bestellung" target="_blank" rel="noreferrer" className="underline">/liane-bestellung</a>
            </div>
            <iframe src="/liane-bestellung" className="w-full h-full border-0" title="Liane Bestell-Widget Vorschau" />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-2xl w-full bg-white p-10 rounded-2xl shadow-xl border border-gray-100">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">
              Liane Box — {aktiverTitel}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              {aktiverText}
            </p>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
              <strong>Status:</strong> Platzhalter. Inhalt folgt in den
              nächsten Schritten.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==========================================================================
// Liane-Bestellungen — Cockpit-Sicht der eingehenden Bestell-Antraege
// ==========================================================================
const STATUS_LABELS = {
  eingegangen: { label: 'Eingegangen', farbe: 'bg-blue-100 text-blue-700' },
  bearbeitet: { label: 'Bearbeitet', farbe: 'bg-amber-100 text-amber-700' },
  an_partner_gesendet: { label: 'An Partner gesendet', farbe: 'bg-emerald-100 text-emerald-700' },
  storniert: { label: 'Storniert', farbe: 'bg-red-100 text-red-700' },
};

function LianeBestellungenAnsicht() {
  const [bestellungen, setBestellungen] = useState([]);
  const [auswahlId, setAuswahlId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [isLoading, setIsLoading] = useState(false);

  const reload = async () => {
    setIsLoading(true);
    try {
      const url = filter === 'alle' ? '/api/liane/bestellungen' : `/api/liane/bestellungen?status=${filter}`;
      const data = await api.get(url);
      setBestellungen(Array.isArray(data) ? data : []);
    } catch (e) {
      alert('Konnte Bestellungen nicht laden: ' + (e?.detail || e?.message));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const ladeDetail = async (id) => {
    setAuswahlId(id);
    setDetail(null);
    try {
      const d = await api.get(`/api/liane/bestellungen/${id}`);
      setDetail(d);
    } catch (e) {
      alert('Detail konnte nicht geladen werden: ' + (e?.detail || e?.message));
    }
  };

  const setStatus = async (status) => {
    if (!detail) return;
    try {
      const d = await api.put(`/api/liane/bestellungen/${detail.id}`, { status });
      setDetail(d);
      await reload();
    } catch (e) {
      alert('Status-Aenderung fehlgeschlagen: ' + (e?.detail || 'unbekannt'));
    }
  };

  const loeschen = async () => {
    if (!detail) return;
    if (!window.confirm(`Bestellung von ${detail.vorname} ${detail.nachname} wirklich loeschen?`)) return;
    try {
      await api.del(`/api/liane/bestellungen/${detail.id}`);
      setDetail(null);
      setAuswahlId(null);
      await reload();
    } catch (e) {
      alert('Loeschen fehlgeschlagen: ' + (e?.detail || 'unbekannt'));
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Liane-Bestellungen</h2>
      <p className="text-gray-500 mb-4 text-sm">
        Eingehende Antraege aus dem Bestell-Widget auf liane-pflegebox.de.
      </p>

      {/* Status-Filter */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'alle', label: 'Alle' },
          { id: 'eingegangen', label: 'Eingegangen' },
          { id: 'bearbeitet', label: 'Bearbeitet' },
          { id: 'an_partner_gesendet', label: 'An Partner' },
          { id: 'storniert', label: 'Storniert' },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
              filter === f.id ? 'bg-fuchsia-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >{f.label}</button>
        ))}
        <button onClick={reload} className="ml-auto px-3 py-1.5 rounded text-xs font-bold text-gray-500 hover:bg-gray-100 border border-gray-300">
          ↻ neu laden
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4" style={{ minHeight: '60vh' }}>
        {/* Liste */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden md:col-span-1">
          {isLoading ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lade…
            </div>
          ) : bestellungen.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              Keine Bestellungen in diesem Filter.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {bestellungen.map(b => (
                <li key={b.id}>
                  <button onClick={() => ladeDetail(b.id)}
                    className={`w-full text-left p-3 hover:bg-blue-50 transition-colors ${
                      auswahlId === b.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-800 text-sm">
                        {b.anrede ? b.anrede + ' ' : ''}{b.vorname} {b.nachname}
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS_LABELS[b.status]?.farbe || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[b.status]?.label || b.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {b.created_at && new Date(b.created_at).toLocaleString('de-DE')} ·
                      {' '}{(b.summe_brutto || 0).toFixed(2)} €
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
          {!auswahlId ? (
            <div className="p-10 text-center text-gray-400 text-sm">Bitte Bestellung links auswaehlen.</div>
          ) : !detail ? (
            <div className="p-10 text-center text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lade Detail…
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-bold text-gray-800">
                    {detail.anrede ? detail.anrede + ' ' : ''}{detail.vorname} {detail.nachname}
                  </div>
                  <div className="text-xs text-gray-500">
                    Bestell-Nr: {detail.id.slice(0, 8).toUpperCase()} ·
                    {' '}eingegangen {new Date(detail.created_at).toLocaleString('de-DE')}
                  </div>
                </div>
                <div className="flex gap-1 items-center flex-wrap">
                  <select value={detail.status} onChange={(e) => setStatus(e.target.value)}
                    className="text-xs font-bold border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  <a href={`/api/liane/bestellungen/${detail.id}/pdf`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-fuchsia-700 text-white rounded hover:bg-fuchsia-800"
                  ><FileText className="w-3.5 h-3.5" /> PDF</a>
                  <button onClick={loeschen}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Loeschen"
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="p-5 overflow-y-auto space-y-4">
                <DetailBlock titel="Pflegebeduerftige Person">
                  <DetailZeile k="Geburtsdatum" v={detail.geburtsdatum ? new Date(detail.geburtsdatum).toLocaleDateString('de-DE') : '—'} />
                  <DetailZeile k="Pflegegrad" v={detail.pflegegrad || '—'} />
                  <DetailZeile k="Krankenkasse" v={detail.krankenkasse} />
                  <DetailZeile k="Versichertennummer" v={detail.versichertennummer} />
                </DetailBlock>
                <DetailBlock titel="Adresse">
                  <DetailZeile k="Strasse" v={`${detail.strasse || ''} ${detail.hausnummer || ''}`.trim()} />
                  <DetailZeile k="PLZ / Ort" v={`${detail.plz || ''} ${detail.stadt || ''}`.trim()} />
                  {detail.lieferadresse_abweichend && (
                    <>
                      <DetailZeile k="Lieferung an" v={`${detail.liefer_vorname || ''} ${detail.liefer_nachname || ''}`.trim()} />
                      <DetailZeile k="Lieferadresse" v={`${detail.liefer_strasse || ''} ${detail.liefer_hausnummer || ''}, ${detail.liefer_plz || ''} ${detail.liefer_stadt || ''}`} />
                    </>
                  )}
                </DetailBlock>
                <DetailBlock titel="Kontaktperson">
                  <DetailZeile k="Name" v={`${detail.kontakt_vorname || ''} ${detail.kontakt_nachname || ''}`.trim()} />
                  <DetailZeile k="E-Mail" v={detail.kontakt_email ? <a href={`mailto:${detail.kontakt_email}`} className="text-blue-600 hover:underline">{detail.kontakt_email}</a> : '—'} />
                  <DetailZeile k="Telefon" v={detail.kontakt_telefon} />
                </DetailBlock>
                <DetailBlock titel={`Warenkorb · Summe ${(detail.summe_brutto || 0).toFixed(2)} €`}>
                  {(detail.warenkorb || []).length === 0 ? (
                    <div className="text-xs text-gray-400 italic">Leer.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left p-2 text-xs font-bold text-gray-600 uppercase">Anzahl</th>
                          <th className="text-left p-2 text-xs font-bold text-gray-600 uppercase">Produkt</th>
                          <th className="text-left p-2 text-xs font-bold text-gray-600 uppercase">Variante</th>
                          <th className="text-right p-2 text-xs font-bold text-gray-600 uppercase">Preis</th>
                          <th className="text-right p-2 text-xs font-bold text-gray-600 uppercase">Topf</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.warenkorb || []).map((z, i) => (
                          <tr key={i} className="border-b border-gray-100">
                            <td className="p-2">{z.anzahl}</td>
                            <td className="p-2 font-medium text-gray-800">{z.titel}</td>
                            <td className="p-2 text-gray-600 text-xs">{z.variante_label}</td>
                            <td className="p-2 text-right">{Number(z.zwischensumme || 0).toFixed(2)} €</td>
                            <td className="p-2 text-right">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                z.topf === 'jaehrlich_4_stueck' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {z.topf === 'jaehrlich_4_stueck' ? 'jährlich 4' : '42 € Topf'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </DetailBlock>
                {detail.unterschrift_png && (
                  <DetailBlock titel="Unterschrift">
                    <img src={`data:image/png;base64,${detail.unterschrift_png}`}
                         alt="Unterschrift"
                         className="bg-white border border-gray-200 rounded max-h-32" />
                  </DetailBlock>
                )}
                <DetailBlock titel="Bestaetigungen">
                  <DetailZeile k="Bedarf bekannt" v={detail.bedarf_bekannt ? '✓' : '—'} />
                  <DetailZeile k="Anbieterwechsel" v={detail.anbieterwechsel ? '✓' : '—'} />
                  <DetailZeile k="Datenschutz" v={detail.datenschutz_akzeptiert ? '✓' : '—'} />
                </DetailBlock>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Liane-Box-Kunden — Kundenverwaltung (Liste + Statistik + CRUD)
// ==========================================================================
const LIANE_STATUS = {
  aktiv:   { label: 'Aktiv',   farbe: 'bg-green-100 text-green-700' },
  pause:   { label: 'Pause',   farbe: 'bg-yellow-100 text-yellow-700' },
  inaktiv: { label: 'Inaktiv', farbe: 'bg-gray-200 text-gray-600' },
};

const LEERER_KUNDE = {
  anrede: '', vorname: '', nachname: '', geburtsdatum: '', telefon: '', email: '',
  strasse: '', plz: '', ort: '', bundesland: '', kasse: '', versichertennummer: '',
  pflegegrad: '', angehoeriger: '', liefer_name: '', liefer_adresse: '',
  status: 'aktiv', aufnahme_am: '',
};

const LIMIT = 30;

function LianeKundenAnsicht() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [auswahl, setAuswahl] = useState(null);
  const [formOffen, setFormOffen] = useState(false);
  const [formKunde, setFormKunde] = useState(null);

  const ladeStats = async () => {
    try { setStats(await api.get('/api/liane/kunden/stats')); }
    catch (e) { console.warn('Liane-Stats laden fehlgeschlagen', e); }
  };

  const ladeListe = async () => {
    setIsLoading(true);
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q.trim());
      if (statusFilter) p.set('status', statusFilter);
      p.set('limit', String(LIMIT));
      p.set('offset', String(offset));
      const d = await api.get(`/api/liane/kunden/liste?${p.toString()}`);
      setItems(Array.isArray(d.items) ? d.items : []);
      setTotal(d.total || 0);
    } catch (e) {
      alert('Kundenliste konnte nicht geladen werden: ' + (e?.detail || e?.message));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { ladeStats(); /* eslint-disable-next-line */ }, []);
  // Debounce: Tippen in der Suche + Filter-/Seitenwechsel laden die Liste neu.
  useEffect(() => {
    const t = setTimeout(ladeListe, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, statusFilter, offset]);

  const speichern = async (payload, id) => {
    // '' -> null, Zahlen konvertieren
    const clean = {};
    Object.entries(payload).forEach(([k, v]) => {
      if (k === 'pflegegrad') clean[k] = v === '' || v == null ? null : Number(v);
      else clean[k] = (v === '' ? null : v);
    });
    if (id) {
      const d = await api.put(`/api/liane/kunden/${id}`, clean);
      setAuswahl(d);
    } else {
      await api.post('/api/liane/kunden', clean);
    }
    setFormOffen(false);
    setFormKunde(null);
    await Promise.all([ladeListe(), ladeStats()]);
  };

  const statusAendern = async (id, status) => {
    try {
      const d = await api.put(`/api/liane/kunden/${id}`, { status });
      setAuswahl(d);
      await Promise.all([ladeListe(), ladeStats()]);
    } catch (e) {
      alert('Status-Aenderung fehlgeschlagen: ' + (e?.detail || 'unbekannt'));
    }
  };

  const loeschen = async (k) => {
    if (!window.confirm(`Liane-Box-Kunde ${k.vorname || ''} ${k.nachname || ''} wirklich endgueltig loeschen?`)) return;
    try {
      await api.del(`/api/liane/kunden/${k.id}`);
      setAuswahl(null);
      await Promise.all([ladeListe(), ladeStats()]);
    } catch (e) {
      alert('Loeschen fehlgeschlagen: ' + (e?.detail || 'unbekannt'));
    }
  };

  const seite = Math.floor(offset / LIMIT) + 1;
  const seitenGesamt = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Liane-Box-Kunden</h2>
          <p className="text-gray-500 text-sm">Stammdaten der Pflegebox-Bestandskunden.</p>
        </div>
        <button
          onClick={() => { setFormKunde({ ...LEERER_KUNDE }); setFormOffen(true); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
          style={{ backgroundColor: CI.brombeer }}
        >
          <Plus className="w-4 h-4" /> Neuer Kunde
        </button>
      </div>

      {stats && <LianeStatistik stats={stats} />}

      {/* Suche + Status-Filter */}
      <div className="flex flex-wrap items-center gap-2 mb-4 mt-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOffset(0); }}
            placeholder="Suche: Name, Ort, E-Mail, Versichertennummer…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-200"
          />
        </div>
        {[
          { id: '', label: 'Alle' },
          { id: 'aktiv', label: 'Aktiv' },
          { id: 'pause', label: 'Pause' },
          { id: 'inaktiv', label: 'Inaktiv' },
        ].map(f => (
          <button key={f.id || 'alle'} onClick={() => { setStatusFilter(f.id); setOffset(0); }}
            className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
              statusFilter === f.id ? 'bg-fuchsia-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >{f.label}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4" style={{ minHeight: '55vh' }}>
        {/* Liste */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden md:col-span-1 flex flex-col">
          <div className="px-3 py-2 border-b border-gray-100 text-xs text-gray-500 flex items-center justify-between">
            <span>{total} Kunden{statusFilter ? ` · ${LIANE_STATUS[statusFilter]?.label}` : ''}</span>
            {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">Keine Treffer.</div>
          ) : (
            <ul className="divide-y divide-gray-100 flex-1 overflow-y-auto">
              {items.map(k => (
                <li key={k.id}>
                  <button onClick={() => setAuswahl(k)}
                    className={`w-full text-left p-3 hover:bg-fuchsia-50 transition-colors ${auswahl?.id === k.id ? 'bg-fuchsia-50' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-gray-800 text-sm truncate">
                        {k.nachname}{k.vorname ? `, ${k.vorname}` : ''}
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${LIANE_STATUS[k.status]?.farbe || 'bg-gray-100 text-gray-600'}`}>
                        {LIANE_STATUS[k.status]?.label || k.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {[k.plz, k.ort].filter(Boolean).join(' ')}{k.pflegegrad ? ` · PG ${k.pflegegrad}` : ''}
                      {k.ist_beratungskunde ? ' · ✓ Beratungskunde' : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {/* Pagination */}
          {total > LIMIT && (
            <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between text-xs">
              <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100">← zurück</button>
              <span className="text-gray-500">Seite {seite} / {seitenGesamt}</span>
              <button disabled={seite >= seitenGesamt} onClick={() => setOffset(offset + LIMIT)}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100">weiter →</button>
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
          {!auswahl ? (
            <div className="p-10 text-center text-gray-400 text-sm">Bitte Kunden links auswaehlen — oder oben „Neuer Kunde".</div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-bold text-gray-800">
                    {auswahl.anrede ? auswahl.anrede + ' ' : ''}{auswahl.vorname} {auswahl.nachname}
                  </div>
                  <div className="text-xs text-gray-500">
                    {auswahl.ist_beratungskunde
                      ? <span className="text-green-700 font-semibold">✓ auch Beratungskunde</span>
                      : 'reiner Liane-Box-Kunde'}
                    {auswahl.aufnahme_am ? ` · aufgenommen ${new Date(auswahl.aufnahme_am).toLocaleDateString('de-DE')}` : ''}
                  </div>
                </div>
                <div className="flex gap-1 items-center flex-wrap">
                  <select value={auswahl.status} onChange={(e) => statusAendern(auswahl.id, e.target.value)}
                    className="text-xs font-bold border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    {Object.entries(LIANE_STATUS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  <button onClick={() => { setFormKunde({ ...LEERER_KUNDE, ...auswahl, geburtsdatum: auswahl.geburtsdatum || '', aufnahme_am: auswahl.aufnahme_am || '' }); setFormOffen(true); }}
                    className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-fuchsia-700 text-white rounded hover:bg-fuchsia-800"
                  ><Pencil className="w-3.5 h-3.5" /> Bearbeiten</button>
                  <button onClick={() => loeschen(auswahl)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Loeschen"
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="p-5 overflow-y-auto space-y-4">
                <DetailBlock titel="Person">
                  <DetailZeile k="Anrede" v={auswahl.anrede} />
                  <DetailZeile k="Geburtsdatum" v={auswahl.geburtsdatum ? new Date(auswahl.geburtsdatum).toLocaleDateString('de-DE') : '—'} />
                  <DetailZeile k="Angehöriger" v={auswahl.angehoeriger} />
                </DetailBlock>
                <DetailBlock titel="Kontakt & Adresse">
                  <DetailZeile k="Telefon" v={auswahl.telefon ? <a href={`tel:${auswahl.telefon}`} className="text-blue-600 hover:underline">{auswahl.telefon}</a> : '—'} />
                  <DetailZeile k="E-Mail" v={auswahl.email ? <a href={`mailto:${auswahl.email}`} className="text-blue-600 hover:underline">{auswahl.email}</a> : '—'} />
                  <DetailZeile k="Strasse" v={auswahl.strasse} />
                  <DetailZeile k="PLZ / Ort" v={[auswahl.plz, auswahl.ort].filter(Boolean).join(' ')} />
                  <DetailZeile k="Bundesland" v={auswahl.bundesland} />
                </DetailBlock>
                <DetailBlock titel="Versicherung & Pflege">
                  <DetailZeile k="Kasse" v={auswahl.kasse} />
                  <DetailZeile k="Versichertennummer" v={auswahl.versichertennummer} />
                  <DetailZeile k="Pflegegrad" v={auswahl.pflegegrad || '—'} />
                </DetailBlock>
                {(auswahl.liefer_name || auswahl.liefer_adresse) && (
                  <DetailBlock titel="Abweichende Lieferadresse">
                    <DetailZeile k="Empfänger" v={auswahl.liefer_name} />
                    <DetailZeile k="Adresse" v={auswahl.liefer_adresse} />
                  </DetailBlock>
                )}
                {/* Platzhalter fuer die Bestellungs-Verknuepfung (naechster Block) */}
                <DetailBlock titel="Bestellungen dieses Kunden">
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    Verknüpfung mit den Liane-Bestellungen folgt im nächsten Schritt —
                    hier siehst du dann, welche Boxen dieser Kunde bezogen hat.
                  </div>
                </DetailBlock>
              </div>
            </>
          )}
        </div>
      </div>

      {formOffen && formKunde && (
        <LianeKundeFormModal
          kunde={formKunde}
          onClose={() => { setFormOffen(false); setFormKunde(null); }}
          onSave={speichern}
        />
      )}
    </div>
  );
}

// Statistik-Leiste: Status-Kacheln + Pflegegrad- und Bundesland-Verteilung.
function LianeStatistik({ stats }) {
  const fmt = (n) => (n == null ? '—' : n.toLocaleString('de-DE'));
  const pgMax = Math.max(1, ...Object.values(stats.pflegegrade || {}));
  const blMax = Math.max(1, ...(stats.bundeslaender || []).map(b => b.anzahl));
  const pgReihe = [['1', 'PG 1'], ['2', 'PG 2'], ['3', 'PG 3'], ['4', 'PG 4'], ['5', 'PG 5'], ['ohne', 'ohne']];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatKachel label="Gesamt" wert={fmt(stats.gesamt)} icon={Users} farbe={CI.brombeer} />
        <StatKachel label="Aktiv" wert={fmt(stats.aktiv)} icon={Package} farbe="#16a34a" />
        <StatKachel label="Pause" wert={fmt(stats.pause)} icon={Package} farbe="#ca8a04" />
        <StatKachel label="Inaktiv" wert={fmt(stats.inaktiv)} icon={Package} farbe="#6b7280" />
        <StatKachel label="Auch Beratungskunde" wert={fmt(stats.auch_beratungskunden)} icon={Activity} farbe="#2DBAE8" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs font-bold text-gray-500 uppercase mb-3">Pflegegrade</div>
          <div className="space-y-1.5">
            {pgReihe.map(([key, label]) => (
              <BalkenZeile key={key} label={label} anzahl={(stats.pflegegrade || {})[key] || 0} max={pgMax} farbe={CI.brombeer} />
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Bundesländer
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {(stats.bundeslaender || []).map(b => (
              <BalkenZeile key={b.name} label={b.name} anzahl={b.anzahl} max={blMax} farbe="#2DBAE8" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatKachel({ label, wert, icon: Icon, farbe }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: farbe }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-bold text-gray-800 tabular-nums leading-none">{wert}</div>
        <div className="text-[11px] text-gray-500 truncate">{label}</div>
      </div>
    </div>
  );
}

function BalkenZeile({ label, anzahl, max, farbe }) {
  const pct = Math.round((anzahl / max) * 100);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-28 text-gray-600 truncate flex-shrink-0">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
        <div className="h-3 rounded-full" style={{ width: `${pct}%`, backgroundColor: farbe }} />
      </div>
      <span className="w-10 text-right font-bold text-gray-700 tabular-nums flex-shrink-0">{anzahl}</span>
    </div>
  );
}

// Modal zum Anlegen / Bearbeiten eines Liane-Box-Kunden.
function LianeKundeFormModal({ kunde, onClose, onSave }) {
  const [form, setForm] = useState(kunde);
  const [saving, setSaving] = useState(false);
  const istNeu = !kunde.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const speichern = async () => {
    if (!form.vorname?.trim() && !form.nachname?.trim()) {
      alert('Bitte mindestens Vor- oder Nachname angeben.');
      return;
    }
    setSaving(true);
    try {
      const { id, kunde_id, ist_beratungskunde, ...rest } = form;
      await onSave(rest, kunde.id || null);
    } catch (e) {
      alert('Speichern fehlgeschlagen: ' + (e?.detail || e?.message || 'unbekannt'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-bold text-gray-800">{istNeu ? 'Neuer Liane-Box-Kunde' : 'Kunde bearbeiten'}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <FeldSelect label="Anrede" v={form.anrede} onChange={v => set('anrede', v)} optionen={['', 'Frau', 'Herr', 'Divers']} />
          <FeldSelect label="Status" v={form.status} onChange={v => set('status', v)} optionen={['aktiv', 'pause', 'inaktiv']} />
          <Feld label="Vorname" v={form.vorname} onChange={v => set('vorname', v)} />
          <Feld label="Nachname" v={form.nachname} onChange={v => set('nachname', v)} />
          <Feld label="Geburtsdatum" type="date" v={form.geburtsdatum} onChange={v => set('geburtsdatum', v)} />
          <Feld label="Pflegegrad" type="number" v={form.pflegegrad} onChange={v => set('pflegegrad', v)} />
          <Feld label="Telefon" v={form.telefon} onChange={v => set('telefon', v)} />
          <Feld label="E-Mail" v={form.email} onChange={v => set('email', v)} />
          <Feld label="Strasse" v={form.strasse} onChange={v => set('strasse', v)} />
          <Feld label="PLZ" v={form.plz} onChange={v => set('plz', v)} />
          <Feld label="Ort" v={form.ort} onChange={v => set('ort', v)} />
          <Feld label="Bundesland" v={form.bundesland} onChange={v => set('bundesland', v)} />
          <Feld label="Kasse" v={form.kasse} onChange={v => set('kasse', v)} />
          <Feld label="Versichertennummer" v={form.versichertennummer} onChange={v => set('versichertennummer', v)} />
          <Feld label="Angehöriger" v={form.angehoeriger} onChange={v => set('angehoeriger', v)} />
          <Feld label="Aufnahme am" type="date" v={form.aufnahme_am} onChange={v => set('aufnahme_am', v)} />
          <Feld label="Liefer-Empfänger" v={form.liefer_name} onChange={v => set('liefer_name', v)} />
          <Feld label="Lieferadresse" v={form.liefer_adresse} onChange={v => set('liefer_adresse', v)} />
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-lg">Abbrechen</button>
          <button onClick={speichern} disabled={saving}
            className="px-4 py-2 text-sm font-bold text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: CI.brombeer }}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {istNeu ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Feld({ label, v, onChange, type = 'text' }) {
  return (
    <label className="text-sm">
      <span className="block text-xs font-semibold text-gray-500 mb-1">{label}</span>
      <input type={type} value={v ?? ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-200" />
    </label>
  );
}

function FeldSelect({ label, v, onChange, optionen }) {
  return (
    <label className="text-sm">
      <span className="block text-xs font-semibold text-gray-500 mb-1">{label}</span>
      <select value={v ?? ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-fuchsia-200">
        {optionen.map(o => <option key={o} value={o}>{o === '' ? '—' : o}</option>)}
      </select>
    </label>
  );
}

function DetailBlock({ titel, children }) {
  return (
    <div>
      <div className="text-xs font-bold text-gray-500 uppercase mb-2">{titel}</div>
      <div className="bg-gray-50/50 rounded p-2">{children}</div>
    </div>
  );
}
function DetailZeile({ k, v }) {
  return (
    <div className="flex text-sm py-0.5">
      <span className="w-44 text-gray-500">{k}</span>
      <span className="text-gray-800">{v || '—'}</span>
    </div>
  );
}
